import { useState } from 'react';

import classNames from 'classnames';

import styles     from './ThemeEditor.module.scss';

import WidgetFrame from '../../widget/navigation/WidgetFrame';
import FieldColor  from '../../widget/inputs/FieldColor';

import { getCSSVarValue } from '../../../utils/utils_color';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface ColorVariable {
  name:             string;
  variable:         string;
  darkVariable:     string;
  currentValue:     string;
  currentDarkValue: string;
}

// ─────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────

const entry = (
  name:         string,
  variable:     string,
  darkVariable: string,
): ColorVariable => ({
  name,
  variable,
  darkVariable,
  currentValue:     getCSSVarValue(variable),
  currentDarkValue: getCSSVarValue(darkVariable),
});

// ─────────────────────────────────────────────
// Composant
// ─────────────────────────────────────────────

export function ThemeEditor() {

  const [colors, setColors] = useState<ColorVariable[]>([
    entry('BodyFond',                              '--color-Body-BG',                                   '--color-dark-Body-BG'                                  ),
    entry('PanelNiv1Fond',                         '--color-Panel-niv1-BG',                             '--color-dark-Panel-niv1-BG'                            ),
    entry('PanelNiv1Bordure',                      '--color-Panel-niv1-Border',                         '--color-dark-Panel-niv1-Border'                        ),
    entry('PanelNiv1Ombre',                        '--color-Panel-niv1-Shadow',                         '--color-dark-Panel-niv1-Shadow'                        ),
    entry('PanelNiv2Fond',                         '--color-Panel-niv2-BG',                             '--color-dark-Panel-niv2-BG'                            ),
    entry('PanelNiv2Bordure',                      '--color-Panel-niv2-Border',                         '--color-dark-Panel-niv2-Border'                        ),
    entry('PanelNiv2Ombre',                        '--color-Panel-niv2-Shadow',                         '--color-dark-Panel-niv2-Shadow'                        ),
    entry('TexteFormeNormal',                      '--color-Text-FG-normal',                            '--color-dark-Text-FG-normal'                           ),
    entry('TexteFormeAccentue',                    '--color-Text-FG-emphasized',                        '--color-dark-Text-FG-emphasized'                       ),
    entry('GraphReplay',                           '--color-Graph-Replay',                              '--color-dark-Graph-Replay'                             ),
    entry('GraphLive',                             '--color-Graph-Live',                                '--color-dark-Graph-Live'                               ),
    entry('BoutonFondInactif',                     '--color-Button-BG-disable',                         '--color-dark-Button-BG-disable'                        ),
    entry('BoutonFormeInactif',                    '--color-Button-FG-disable',                         '--color-dark-Button-FG-disable'                        ),
    entry('BoutonFondRepos',                       '--color-Button-BG-idle',                            '--color-dark-Button-BG-idle'                           ),
    entry('BoutonFormeRepos',                      '--color-Button-FG-idle',                            '--color-dark-Button-FG-idle'                           ),
    entry('BoutonFondSurvole',                     '--color-Button-BG-hover',                           '--color-dark-Button-BG-hover'                          ),
    entry('BoutonFormeSurvole',                    '--color-Button-FG-hover',                           '--color-dark-Button-FG-hover'                          ),
    entry('BoutonFondSelectionne',                 '--color-Button-BG-select',                          '--color-dark-Button-BG-select'                         ),
    entry('BoutonFormeSelectionne',                '--color-Button-FG-select',                          '--color-dark-Button-FG-select'                         ),
    entry('ChampSaisieFondInactif',                '--color-Field-BG-disable',                          '--color-dark-Field-BG-disable'                         ),
    entry('ChampSaisieFormeInactif',               '--color-Field-FG-disable',                          '--color-dark-Field-FG-disable'                         ),
    entry('ChampSaisieIconInactif',                '--color-Field-Icon-disable',                        '--color-dark-Field-Icon-disable'                       ),
    entry('ChampSaisieBordureInactif',             '--color-Field-Border-disable',                      '--color-dark-Field-Border-disable'                     ),
    entry('ChampSaisieFondNormal',                 '--color-Field-BG-normal',                           '--color-dark-Field-BG-normal'                          ),
    entry('ChampSaisieFormeNormal',                '--color-Field-FG-normal',                           '--color-dark-Field-FG-normal'                          ),
    entry('ChampSaisieBordureNormal',              '--color-Field-Border-normal',                       '--color-dark-Field-Border-normal'                      ),
    entry('ChampSaisieFocusNormal',                '--color-Field-Focus-normal',                        '--color-dark-Field-Focus-normal'                       ),
    entry('ChampSaisieCaretNormal',                '--color-Field-Caret-normal',                        '--color-dark-Field-Caret-normal'                       ),
    entry('ChampSaisieIconNormal',                 '--color-Field-Icon-normal',                         '--color-dark-Field-Icon-normal'                        ),
    entry('ChampSaisieIconHoverNormal',            '--color-Field-Icon-hover-normal',                   '--color-dark-Field-Icon-hover-normal'                  ),
    entry('ChampSaisieIconSelectedNormal',         '--color-Field-Icon-selected-normal',                '--color-dark-Field-Icon-selected-normal'               ),
    entry('ZoneSaisieFondInactif',                 '--color-TextArea-BG-disable',                       '--color-dark-TextArea-BG-disable'                      ),
    entry('ZoneSaisieFormeInactif',                '--color-TextArea-FG-disable',                       '--color-dark-TextArea-FG-disable'                      ),
    entry('ZoneSaisieBordureInactif',              '--color-TextArea-Border-disable',                   '--color-dark-TextArea-Border-disable'                  ),
    entry('ZoneSaisieFondNormal',                  '--color-TextArea-BG-normal',                        '--color-dark-TextArea-BG-normal'                       ),
    entry('ZoneSaisieFormeNormal',                 '--color-TextArea-FG-normal',                        '--color-dark-TextArea-FG-normal'                       ),
    entry('ZoneSaisieBordureNormal',               '--color-TextArea-Border-normal',                    '--color-dark-TextArea-Border-normal'                   ),
    entry('ZoneSaisieFocusNormal',                 '--color-TextArea-Focus-normal',                     '--color-dark-TextArea-Focus-normal'                    ),
    entry('ZoneSaisieCaretNormal',                 '--color-TextArea-Caret-normal',                     '--color-dark-TextArea-Caret-normal'                    ),
    entry('SelectFondInactif',                     '--color-Select-BG-disable',                         '--color-dark-Select-BG-disable'                        ),
    entry('SelectFormeInactif',                    '--color-Select-FG-disable',                         '--color-dark-Select-FG-disable'                        ),
    entry('SelectOptionFondInactif',               '--color-Select-Option-BG-disable',                  '--color-dark-Select-Option-BG-disable'                 ),
    entry('SelectOptionFormeInactif',              '--color-Select-Option-FG-disable',                  '--color-dark-Select-Option-FG-disable'                 ),
    entry('SelectBorderInactif',                   '--color-Select-Border-disable',                     '--color-dark-Select-Border-disable'                    ),
    entry('SelectFondNormal',                      '--color-Select-BG-normal',                          '--color-dark-Select-BG-normal'                         ),
    entry('SelectFormeNormal',                     '--color-Select-FG-normal',                          '--color-dark-Select-FG-normal'                         ),
    entry('SelectBorderNormal',                    '--color-Select-Border-normal',                      '--color-dark-Select-Border-normal'                     ),
    entry('SelectFocusNormal',                     '--color-Select-Focus-normal',                       '--color-dark-Select-Focus-normal'                      ),
    entry('SelectCaretNormal',                     '--color-Select-Caret-normal',                       '--color-dark-Select-Caret-normal'                      ),
    entry('SelectOptionFondNormal',                '--color-Select-Option-BG-normal',                   '--color-dark-Select-Option-BG-normal'                  ),
    entry('SelectOptionFormeNormal',               '--color-Select-Option-FG-normal',                   '--color-dark-Select-Option-FG-normal'                  ),
    entry('SelectOptionHoverFondNormal',           '--color-Select-Option-Hover-BG-normal',             '--color-dark-Select-Option-Hover-BG-normal'            ),
    entry('SelectOptionHoverFormeNormal',          '--color-Select-Option-Hover-FG-normal',             '--color-dark-Select-Option-Hover-FG-normal'            ),
    entry('OSMZoomForme',                          '--color-OpenStreeMap-Zoom-FG',                      '--color-dark-OpenStreeMap-Zoom-FG'                     ),
    entry('OSMZoomFond',                           '--color-OpenStreeMap-Zoom-BG',                      '--color-dark-OpenStreeMap-Zoom-BG'                     ),
    entry('OSMZoomBorder',                         '--color-OpenStreeMap-Zoom-Border',                  '--color-dark-OpenStreeMap-Zoom-Border'                 ),
    entry('OSMZoomHoverForme',                     '--color-OpenStreeMap-Zoom-Hover-FG',                '--color-dark-OpenStreeMap-Zoom-Hover-FG'               ),
    entry('OSMZoomHoverFond',                      '--color-OpenStreeMap-Zoom-Hover-BG',                '--color-dark-OpenStreeMap-Zoom-Hover-BG'               ),
    entry('TabularGroupHeaderForme',               '--color-Tabular-groupHeader-FG',                    '--color-dark-Tabular-groupHeader-FG'                   ),
    entry('TabularGroupHeaderFond',                '--color-Tabular-groupHeader-BG',                    '--color-dark-Tabular-groupHeader-BG'                   ),
    entry('TabularGroupHeaderBorder',              '--color-Tabular-groupHeader-Border',                '--color-dark-Tabular-groupHeader-Border'               ),
    entry('TabularGroupHeaderShadow',              '--color-Tabular-groupHeader-Shadow',                '--color-dark-Tabular-groupHeader-Shadow'               ),
    entry('TabularGroupHeaderHoverForme',          '--color-Tabular-groupHeader-hover-FG',              '--color-dark-Tabular-groupHeader-hover-FG'             ),
    entry('TabularGroupHeaderHoverFond',           '--color-Tabular-groupHeader-hover-BG',              '--color-dark-Tabular-groupHeader-hover-BG'             ),
    entry('TabularGroupHeaderHoverBorder',         '--color-Tabular-groupHeader-hover-Border',          '--color-dark-Tabular-groupHeader-hover-Border'         ),
    entry('TabularGroupHeaderHoverShadow',         '--color-Tabular-groupHeader-hover-Shadow',          '--color-dark-Tabular-groupHeader-hover-Shadow'         ),
    entry('TabularGroupHeaderExpandedForme',       '--color-Tabular-groupHeader-expanded-FG',           '--color-dark-Tabular-groupHeader-expanded-FG'          ),
    entry('TabularGroupHeaderExpandedFond',        '--color-Tabular-groupHeader-expanded-BG',           '--color-dark-Tabular-groupHeader-expanded-BG'          ),
    entry('TabularGroupHeaderExpandedBorder',      '--color-Tabular-groupHeader-expanded-Border',       '--color-dark-Tabular-groupHeader-expanded-Border'      ),
    entry('TabularGroupHeaderExpandedShadow',      '--color-Tabular-groupHeader-expanded-hover-Shadow', '--color-dark-Tabular-groupHeader-expanded-hover-Shadow'),
    entry('TabularGroupHeaderExpandedHoverForme',  '--color-Tabular-groupHeader-expanded-hover-FG',     '--color-dark-Tabular-groupHeader-expanded-hover-FG'    ),
    entry('TabularGroupHeaderExpandedHoverFond',   '--color-Tabular-groupHeader-expanded-hover-BG',     '--color-dark-Tabular-groupHeader-expanded-hover-BG'    ),
    entry('TabularGroupHeaderExpandedHoverBorder', '--color-Tabular-groupHeader-expanded-hover-Border', '--color-dark-Tabular-groupHeader-expanded-hover-Border'),
    entry('TabularTableFond',                      '--color-Tabular-table-BG',                          '--color-dark-Tabular-table-BG'                         ),
    entry('TabularTableTHForme',                   '--color-Tabular-table-TH-FG',                       '--color-dark-Tabular-table-TH-FG'                      ),
    entry('TabularTableTHFond',                    '--color-Tabular-table-TH-BG',                       '--color-dark-Tabular-table-TH-BG'                      ),
    entry('TabularTableTHBorderBottom',            '--color-Tabular-table-TH-BorderBottom',             '--color-dark-Tabular-table-TH-BorderBottom'            ),
    entry('TabularTableTDBorderBottom',            '--color-Tabular-table-TD-BorderBottom',             '--color-dark-Tabular-table-TD-BorderBottom'            ),
    entry('NightAndDayToggleOn',                   '--color-NightAndDayToggle-On',                      '--color-dark-NightAndDayToggle-On'                     ),
    entry('NightAndDayToggleOff',                  '--color-NightAndDayToggle-Off',                     '--color-dark-NightAndDayToggle-Off'                    ),
    entry('NightAndDayToggleIconOn',               '--color-NightAndDayToggle-Icon-On',                 '--color-dark-NightAndDayToggle-Icon-On'                ),
    entry('NightAndDayToggleIconOff',              '--color-NightAndDayToggle-Icon-Off',                '--color-dark-NightAndDayToggle-Icon-Off'               ),
    entry('TabIndexActiveBorderBottom',            '--color-Tab-Index-Active-BorderBottom',             '--color-dark-Tab-Index-Active-BorderBottom'            ),
    entry('TabIndexActiveFG',                      '--color-Tab-Index-Active-FG',                       '--color-dark-Tab-Index-Active-FG'                      ),
    entry('TabIndexInactiveFG',                    '--color-Tab-Index-Inactive-FG',                     '--color-dark-Tab-Index-Inactive-FG'                    ),
    entry('IconForme',                             '--color-Icon-FG',                                   '--color-dark-Icon-FG'                                  ),
    entry('IconHoverForme',                        '--color-Icon-hover-FG',                             '--color-dark-Icon-hover-FG'                            ),
    entry('ColorNOK',                              '--color-NOK',                                       '--color-dark-NOK'                                      ),
    entry('ColorOK',                               '--color-OK',                                        '--color-dark-OK'                                       ),
    entry('ColorEmphasis',                         '--color-Emphasis',                                  '--color-dark-Emphasis'                                 ),
    entry('LinkForme',                             '--color-Link-FG',                                   '--color-dark-Link-FG'                                  ),
    entry('LinkHoverForme',                        '--color-Link-Hover-FG',                             '--color-dark-Link-Hover-FG'                            ),
    entry('VideoTranscode',                        '--color-Video-Transcoded',                          '--color-dark-Video-Transcoded'                         ),
    entry('VideoNonTranscode',                     '--color-Video-NotTranscoded',                       '--color-dark-Video-NotTranscoded'                      ),
  ]);

  // ─── Handlers ───────────────────────────────

  const changeLight = (name: string, newColor: string) => {
    const index = colors.findIndex(c => c.name === name);
    if (index === -1) { console.warn(`Light: "${name}" introuvable`); return; }
    document.documentElement.style.setProperty(colors[index].variable, newColor);
    setColors(prev => {
      const next = [...prev];
      next[index] = { ...next[index], currentValue: newColor };
      localStorage.setItem('customTheme', JSON.stringify(next));
      return next;
    });
  };

  const changeDark = (name: string, newColor: string) => {
    const index = colors.findIndex(c => c.name === name);
    if (index === -1) { console.warn(`Dark: "${name}" introuvable`); return; }
    document.documentElement.style.setProperty(colors[index].darkVariable, newColor);
    setColors(prev => {
      const next = [...prev];
      next[index] = { ...next[index], currentDarkValue: newColor };
      localStorage.setItem('customTheme', JSON.stringify(next));
      return next;
    });
  };

  const getLight = (name: string): string =>
    colors.find(c => c.name === name)?.currentValue ?? '';

  const getDark = (name: string): string =>
    colors.find(c => c.name === name)?.currentDarkValue ?? '';

  // ─── Export / Import ────────────────────────

  const downloadFile = (filename: string, content: string, type = 'application/json') => {
    const blob = new Blob([content], { type });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () =>
    downloadFile('theme.json', JSON.stringify(colors, null, 2));

  const exportCSS = () => {
    const light = colors.map(c => `  ${c.variable}: ${c.currentValue};`).join('\n');
    const dark  = colors.map(c => `  ${c.darkVariable}: ${c.currentDarkValue};`).join('\n');
    const css   = `:root {\n${light}\n}\n\n:root[data-theme="dark"] {\n${dark}\n}`;
    downloadFile('theme.css.txt', css, 'text/plain');
  };

  const importJSON = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported: ColorVariable[] = JSON.parse(e.target?.result as string);
        setColors(prev => {
          const next = prev.map(color => {
            const match = imported.find(c => c.name === color.name);
            if (!match) return color;
            document.documentElement.style.setProperty(color.variable,     match.currentValue);
            document.documentElement.style.setProperty(color.darkVariable, match.currentDarkValue);
            return { ...color, currentValue: match.currentValue, currentDarkValue: match.currentDarkValue };
          });
          localStorage.setItem('customTheme', JSON.stringify(next));
          return next;
        });
      } catch {
        alert('Fichier JSON invalide');
      }
    };
    reader.readAsText(file);
  };

  const resetColors = () => {
    colors.forEach(c => {
      document.documentElement.style.removeProperty(c.variable);
      document.documentElement.style.removeProperty(c.darkVariable);
    });
    window.location.reload();
  };

  // ─────────────────────────────────────────────
  // ColorRow
  //
  // WidgetFrame ne peut passer qu'UN seul enfant dans sa colonne droite
  // (renderElement prend le dernier enfant).
  // On wrappe donc les deux FieldColor dans un seul <div> qui est
  // l'unique enfant passé à WidgetFrame.
  // ─────────────────────────────────────────────

  const ColorRow = ({ label, name }: { label: string; name: string }) => (
    <WidgetFrame
      name               = {label}
      labelTextPosition  = "right"
      labelBlockPosition = "left"
      labelVerticalAlign = "center"
      classNameGrid      = {classNames(styles.color_row)}
    >
      {/* Unique enfant → WidgetFrame le place dans sa colonne "element" */}
      <div className={classNames(styles.color_row_fields)}>
        <FieldColor
          showField = {true}
          width     = "150px"
          value     = {getLight(name)}
          onChange  = {(e) => changeLight(name, e.target.value)}
        />
        <FieldColor
          showField = {true}
          width     = "150px"
          value     = {getDark(name)}
          onChange  = {(e) => changeDark(name, e.target.value)}
        />
      </div>
    </WidgetFrame>
  );

  // ─── En-tête Light / Dark (aligné sur les FieldColor) ───────────────

  const ColumnHeaders = () => (
    <div className={classNames(styles.column_headers)}>
      {/* spacer qui mime la largeur du label dans color_row */}
      <div className={classNames(styles.column_headers_spacer)} />
      <div className={classNames(styles.column_headers_titles)}>
        <span className={classNames(styles.col_title)}>☀ Light</span>
        <span className={classNames(styles.col_title)}>☾ Dark</span>
      </div>
    </div>
  );

  // ─── Sections ────────────────────────────────

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <WidgetFrame
      name               = {`${title} :`}
      labelTextPosition  = "right"
      labelBlockPosition = "left"
      classNameGrid      = {classNames(styles.grid_frame)}
    >
      <div className={classNames(styles.block)}>
        <ColumnHeaders />
        {children}
      </div>
    </WidgetFrame>
  );

  const SubSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <WidgetFrame
      name               = {`${title} :`}
      labelTextPosition  = "right"
      labelBlockPosition = "left"
      classNameGrid      = {classNames(styles.grid_frame)}
    >
      <div className={classNames(styles.block)}>
        {children}
      </div>
    </WidgetFrame>
  );

  // ─── Rendu ──────────────────────────────────

  return (
    <div className={classNames(styles.block)}>

      <Section title="Body et zones de découpage">
        <SubSection title="Body">
          <ColorRow label="Fond" name="BodyFond" />
        </SubSection>
        <SubSection title="Panel niveau 1">
          <ColorRow label="Fond"    name="PanelNiv1Fond"    />
          <ColorRow label="Bordure" name="PanelNiv1Bordure" />
          <ColorRow label="Ombre"   name="PanelNiv1Ombre"   />
        </SubSection>
        <SubSection title="Panel niveau 2">
          <ColorRow label="Fond"    name="PanelNiv2Fond"    />
          <ColorRow label="Bordure" name="PanelNiv2Bordure" />
          <ColorRow label="Ombre"   name="PanelNiv2Ombre"   />
        </SubSection>
      </Section>

      <Section title="Textes et labels">
        <SubSection title="Normal">
          <ColorRow label="Forme" name="TexteFormeNormal" />
        </SubSection>
        <SubSection title="Accentué">
          <ColorRow label="Forme" name="TexteFormeAccentue" />
        </SubSection>
      </Section>

      <Section title="Graphiques">
        <SubSection title="Replay">
          <ColorRow label="Forme" name="GraphReplay" />
        </SubSection>
        <SubSection title="Live">
          <ColorRow label="Forme" name="GraphLive" />
        </SubSection>
      </Section>

      <Section title="Bouton">
        <SubSection title="Désactivé">
          <ColorRow label="Fond"  name="BoutonFondInactif"  />
          <ColorRow label="Forme" name="BoutonFormeInactif" />
        </SubSection>
        <SubSection title="Repos">
          <ColorRow label="Fond"  name="BoutonFondRepos"  />
          <ColorRow label="Forme" name="BoutonFormeRepos" />
        </SubSection>
        <SubSection title="Survolé">
          <ColorRow label="Fond"  name="BoutonFondSurvole"  />
          <ColorRow label="Forme" name="BoutonFormeSurvole" />
        </SubSection>
        <SubSection title="Sélectionné">
          <ColorRow label="Fond"  name="BoutonFondSelectionne"  />
          <ColorRow label="Forme" name="BoutonFormeSelectionne" />
        </SubSection>
      </Section>

      <Section title="Champ de saisie">
        <SubSection title="Désactivé">
          <ColorRow label="Fond"    name="ChampSaisieFondInactif"    />
          <ColorRow label="Forme"   name="ChampSaisieFormeInactif"   />
          <ColorRow label="Icône"   name="ChampSaisieIconInactif"    />
          <ColorRow label="Bordure" name="ChampSaisieBordureInactif" />
        </SubSection>
        <SubSection title="Normal">
          <ColorRow label="Fond"            name="ChampSaisieFondNormal"         />
          <ColorRow label="Forme"           name="ChampSaisieFormeNormal"        />
          <ColorRow label="Bordure"         name="ChampSaisieBordureNormal"      />
          <ColorRow label="Focus"           name="ChampSaisieFocusNormal"        />
          <ColorRow label="Curseur"         name="ChampSaisieCaretNormal"        />
          <ColorRow label="Icône"           name="ChampSaisieIconNormal"         />
          <ColorRow label="Icône survol"    name="ChampSaisieIconHoverNormal"    />
          <ColorRow label="Icône sélection" name="ChampSaisieIconSelectedNormal" />
        </SubSection>
      </Section>

      <Section title="Zone de saisie">
        <SubSection title="Désactivé">
          <ColorRow label="Fond"    name="ZoneSaisieFondInactif"    />
          <ColorRow label="Forme"   name="ZoneSaisieFormeInactif"   />
          <ColorRow label="Bordure" name="ZoneSaisieBordureInactif" />
        </SubSection>
        <SubSection title="Normal">
          <ColorRow label="Fond"    name="ZoneSaisieFondNormal"    />
          <ColorRow label="Forme"   name="ZoneSaisieFormeNormal"   />
          <ColorRow label="Bordure" name="ZoneSaisieBordureNormal" />
          <ColorRow label="Focus"   name="ZoneSaisieFocusNormal"   />
          <ColorRow label="Curseur" name="ZoneSaisieCaretNormal"   />
        </SubSection>
      </Section>

      <Section title="Menu déroulant">
        <SubSection title="Désactivé">
          <ColorRow label="Fond du menu"      name="SelectFondInactif"        />
          <ColorRow label="Forme du menu"     name="SelectFormeInactif"       />
          <ColorRow label="Bordure du menu"   name="SelectBorderInactif"      />
          <ColorRow label="Fond de l'option"  name="SelectOptionFondInactif"  />
          <ColorRow label="Forme de l'option" name="SelectOptionFormeInactif" />
        </SubSection>
        <SubSection title="Normal">
          <ColorRow label="Fond du menu"          name="SelectFondNormal"             />
          <ColorRow label="Forme du menu"         name="SelectFormeNormal"            />
          <ColorRow label="Bordure du menu"       name="SelectBorderNormal"           />
          <ColorRow label="Focus du menu"         name="SelectFocusNormal"            />
          <ColorRow label="Caret du menu"         name="SelectCaretNormal"            />
          <ColorRow label="Fond d'une option"     name="SelectOptionFondNormal"       />
          <ColorRow label="Forme d'une option"    name="SelectOptionFormeNormal"      />
          <ColorRow label="Fond option survolée"  name="SelectOptionHoverFondNormal"  />
          <ColorRow label="Forme option survolée" name="SelectOptionHoverFormeNormal" />
        </SubSection>
      </Section>

      <Section title="Carte OpenStreetMap">
        <SubSection title="Zoom">
          <ColorRow label="Fond"         name="OSMZoomFond"       />
          <ColorRow label="Forme"        name="OSMZoomForme"      />
          <ColorRow label="Bordure"      name="OSMZoomBorder"     />
          <ColorRow label="Fond survol"  name="OSMZoomHoverFond"  />
          <ColorRow label="Forme survol" name="OSMZoomHoverForme" />
        </SubSection>
      </Section>

      <Section title="Tableaux">
        <SubSection title="En-tête de groupe">
          <ColorRow label="Forme"   name="TabularGroupHeaderForme"  />
          <ColorRow label="Fond"    name="TabularGroupHeaderFond"   />
          <ColorRow label="Bordure" name="TabularGroupHeaderBorder" />
          <ColorRow label="Ombre"   name="TabularGroupHeaderShadow" />
        </SubSection>
        <SubSection title="En-tête de groupe (survol)">
          <ColorRow label="Forme"   name="TabularGroupHeaderHoverForme"  />
          <ColorRow label="Fond"    name="TabularGroupHeaderHoverFond"   />
          <ColorRow label="Bordure" name="TabularGroupHeaderHoverBorder" />
          <ColorRow label="Ombre"   name="TabularGroupHeaderHoverShadow" />
        </SubSection>
        <SubSection title="En-tête de groupe (déplié)">
          <ColorRow label="Forme"   name="TabularGroupHeaderExpandedForme"  />
          <ColorRow label="Fond"    name="TabularGroupHeaderExpandedFond"   />
          <ColorRow label="Bordure" name="TabularGroupHeaderExpandedBorder" />
          <ColorRow label="Ombre"   name="TabularGroupHeaderExpandedShadow" />
        </SubSection>
        <SubSection title="En-tête de groupe (déplié + survol)">
          <ColorRow label="Forme"   name="TabularGroupHeaderExpandedHoverForme"  />
          <ColorRow label="Fond"    name="TabularGroupHeaderExpandedHoverFond"   />
          <ColorRow label="Bordure" name="TabularGroupHeaderExpandedHoverBorder" />
        </SubSection>
        <SubSection title="Table">
          <ColorRow label="Fond"                name="TabularTableFond"           />
          <ColorRow label="En-tête forme"       name="TabularTableTHForme"        />
          <ColorRow label="En-tête fond"        name="TabularTableTHFond"         />
          <ColorRow label="En-tête bordure bas" name="TabularTableTHBorderBottom" />
          <ColorRow label="Cellule bordure bas" name="TabularTableTDBorderBottom" />
        </SubSection>
      </Section>

      <Section title="Toggle nuit et jour">
        <SubSection title="Activé">
          <ColorRow label="Fond"  name="NightAndDayToggleOn"     />
          <ColorRow label="Icône" name="NightAndDayToggleIconOn" />
        </SubSection>
        <SubSection title="Désactivé">
          <ColorRow label="Fond"  name="NightAndDayToggleOff"     />
          <ColorRow label="Icône" name="NightAndDayToggleIconOff" />
        </SubSection>
      </Section>

      <Section title="Onglets">
        <SubSection title="Actif">
          <ColorRow label="Forme"       name="TabIndexActiveFG"           />
          <ColorRow label="Bordure bas" name="TabIndexActiveBorderBottom" />
        </SubSection>
        <SubSection title="Inactif">
          <ColorRow label="Forme" name="TabIndexInactiveFG" />
        </SubSection>
      </Section>

      <Section title="Icônes">
        <SubSection title="Normal">
          <ColorRow label="Forme" name="IconForme" />
        </SubSection>
        <SubSection title="Survol">
          <ColorRow label="Forme" name="IconHoverForme" />
        </SubSection>
      </Section>

      <Section title="Divers">
        <ColorRow label="Erreur (NOK)" name="ColorNOK"      />
        <ColorRow label="Succès (OK)"  name="ColorOK"       />
        <ColorRow label="Emphase"      name="ColorEmphasis" />
      </Section>

      <Section title="Liens">
        <ColorRow label="Normal" name="LinkForme"      />
        <ColorRow label="Survol" name="LinkHoverForme" />
      </Section>

      <Section title="Vidéo">
        <ColorRow label="Transcodée"     name="VideoTranscode"    />
        <ColorRow label="Non transcodée" name="VideoNonTranscode" />
      </Section>

      {/* ── Actions ── */}
      <input
        type     = "file"
        accept   = ".json"
        style    = {{ display: 'none' }}
        id       = "importTheme"
        onChange = {importJSON}
      />
      <button onClick={() => document.getElementById('importTheme')?.click()}>
        Importer un thème JSON
      </button>
      <button onClick={exportJSON}>Exporter JSON</button>
      <button onClick={exportCSS}>Exporter CSS</button>
      <button onClick={resetColors}>Réinitialiser les couleurs</button>

    </div>
  );
}

export default ThemeEditor;